import type { EventMapPoint } from '@racemap/utilities/api-client';
import { RacemapColors } from '@racemap/utilities/consts/common';
import { OneSecondInMillis } from '@racemap/utilities/consts/time';
import type { FeatureCollection, Point } from 'geojson';
import { type GeoJSONSource, LngLat, type Map as MaplibreMap } from 'maplibre-gl';
import { type FC, useEffect, useMemo, useRef, useState } from 'react';
import MapGL, {
  Layer,
  type MapMouseEvent,
  type MapRef,
  NavigationControl,
  Popup,
  Source,
  type ViewStateChangeEvent,
} from 'react-map-gl/maplibre';
import { trackEvent } from 'src/lib/googleAnalytics';
import { HOVER_STROKE, MAP_STYLE } from './eventsMapStyle';
import { dateRangeLabel, type EventStatus, pointsBounds, statusOf } from './eventsOverview';
// Open Sans Semibold (SIL Open Font License) as MapLibre glyphs, taken from demotiles.maplibre.org.
import glyphsUrl from './glyphs/open-sans-semibold-0-255.pbf?url';

export const STATUS_COLORS: Record<EventStatus, string> = {
  live: '#62bd19',
  upcoming: RacemapColors.PaleBlue,
  past: '#aaaaaa',
};

const STATUS_LABELS: Record<EventStatus, string> = {
  live: 'Live now',
  upcoming: 'Upcoming',
  past: 'Past',
};

// ponytail: one bundled range (Latin-1) for every request; cluster counts only need digits and "k".
// Absolute, because MapLibre loads glyphs from a worker.
const GLYPHS_URL = new URL(glyphsUrl, window.location.href).href;
const transformRequest = (url: string) => ({
  url: url.startsWith('bundled://') ? GLYPHS_URL : url,
});

const isTouch = window.matchMedia('(pointer: coarse)').matches;

// From northern Greenland to Tierra del Fuego; Antarctica is left out.
const NORTH = 84;
const SOUTH = -57;
const WORLD_BOUNDS: [[number, number], [number, number]] = [
  [-180, SOUTH],
  [180, NORTH],
];
const MAP_HEIGHT = 420;
const WORLD_PADDING = 0;
const TILE_SIZE = 512;

// Once a filtered result settles, ease the camera to it: a world view hides every event inside a
// cluster. The delay stops per-keystroke moves; the zoom cap keeps one result at a region.
const FRAME_DELAY_MS = 1.5 * OneSecondInMillis;
const FRAME_DURATION_MS = 1.2 * OneSecondInMillis;
const FRAME_MAX_ZOOM = 8;
const EVENTS_PADDING = 40;

// Web Mercator in world fractions: x from 0 (180°W) to 1, y from 0 (north pole) to 1.
const mercatorX = (lng: number) => (lng + 180) / 360;
const mercatorY = (lat: number) =>
  (1 - Math.log(Math.tan(Math.PI / 4 + (lat * Math.PI) / 360)) / Math.PI) / 2;
const lngOf = (x: number) => x * 360 - 180;
const latOf = (y: number) => (360 / Math.PI) * Math.atan(Math.exp(Math.PI * (1 - 2 * y))) - 90;

// Keeps [lo, hi] in view; when the view is larger than the range, centres it.
const fitRange = (v: number, lo: number, hi: number, view: number) =>
  hi - lo <= view ? (lo + hi) / 2 : Math.min(hi - view / 2, Math.max(lo + view / 2, v));

/**
 * MapLibre's own constraint never zooms out past the map's width when the world is shown once,
 * which would crop the north and south of a wide map. This one centres the world instead.
 */
const constrainToWorld =
  (map: MaplibreMap) =>
  (lngLat: LngLat, zoom: number): { center: LngLat; zoom: number } => {
    const z = Math.max(zoom, map.getMinZoom());
    const worldSize = TILE_SIZE * 2 ** z;
    const { clientWidth, clientHeight } = map.getContainer();
    const x = fitRange(mercatorX(lngLat.lng), 0, 1, clientWidth / worldSize);
    const y = fitRange(
      mercatorY(lngLat.lat),
      mercatorY(NORTH),
      mercatorY(SOUTH),
      clientHeight / worldSize,
    );
    return { center: new LngLat(lngOf(x), latOf(y)), zoom: z };
  };
const SOURCE_ID = 'events';

const round = (n: number) => Math.round(n * 1e4) / 1e4;
const clamp = (n: number, limit: number) => Math.max(-limit, Math.min(limit, n));

/** Camera showing the whole world; also the furthest the user can zoom out. */
const worldCamera = (map: MaplibreMap) => {
  const camera = map.cameraForBounds(WORLD_BOUNDS, { padding: WORLD_PADDING });
  if (camera?.zoom != null) map.setMinZoom(camera.zoom);
  return camera;
};

const fitWorld = (map: MaplibreMap) => {
  const camera = worldCamera(map);
  if (camera) map.jumpTo(camera);
};

const isWholeWorld = (map: MaplibreMap, zoom: number) => zoom - map.getMinZoom() < 0.01;

type Props = {
  points: Array<EventMapPoint>;
  hoveredId: string | null;
  /** Called after the user moved the map, with `<south>,<west>,<north>,<east>`, or undefined once zoomed out to the whole world. */
  onAreaChange: (bbox: string | undefined) => void;
  /** Changing it flies back to the whole world. */
  resetToken: number;
  /** While the list follows the map area, a resize keeps the area instead of refitting the world. */
  areaActive: boolean;
};

export const EventsMap: FC<Props> = ({
  points,
  hoveredId,
  onAreaChange,
  resetToken,
  areaActive,
}) => {
  const mapRef = useRef<MapRef>(null);
  // A cluster click zooms by code, yet the user asked for it: its move filters the list too.
  const clusterZoom = useRef(false);
  // Zoom after the last move: a no-op move fires no movestart, so compare to this instead.
  const lastZoom = useRef(0);
  const [popup, setPopup] = useState<EventMapPoint | null>(null);
  const [cursor, setCursor] = useState<string>('');
  const [hoveredCluster, setHoveredCluster] = useState<number | null>(null);

  const data = useMemo<FeatureCollection<Point>>(() => {
    const now = Date.now();
    return {
      type: 'FeatureCollection',
      features: points.map((p) => ({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [p.lng, p.lat] },
        properties: { id: p.id, status: statusOf(p, now) },
      })),
    };
  }, [points]);

  // Card hover highlights the event's dot.
  useEffect(() => {
    const map = mapRef.current;
    if (map == null || hoveredId == null || !map.getSource(SOURCE_ID)) return;
    map.setFeatureState({ source: SOURCE_ID, id: hoveredId }, { hover: true });
    return () => {
      if (map.getSource(SOURCE_ID)) {
        map.setFeatureState({ source: SOURCE_ID, id: hoveredId }, { hover: false });
      }
    };
  }, [hoveredId]);

  // Cluster ids are numbered per data set, so a hover from the last one can name a different cluster.
  // Keyed on the ids, not the array: the page hands over a fresh array on every render.
  const pointIds = points.map((p) => p.id).join(',');
  useEffect(() => setHoveredCluster(null), [pointIds]);

  const framedBounds = useRef<string | null>(null);
  const framed = useRef(false);
  const pointsKey = useRef<string | null>(null);

  useEffect(() => {
    const map = mapRef.current?.getMap();
    if (resetToken > 0 && map != null) {
      // A reset asks for the world: hand resize fitting back to the map, and treat what is on
      // screen as framed, so a frame already scheduled cannot pull the camera away again.
      framed.current = false;
      framedBounds.current = pointsKey.current;
      const camera = worldCamera(map);
      if (camera) map.flyTo(camera);
    }
  }, [resetToken]);

  useEffect(() => {
    const bounds = pointsBounds(points);
    // Remembered even while the area filter suppresses framing, so a reset can mark it framed.
    pointsKey.current = bounds?.flat().join(',') ?? null;
    if (bounds == null) return;
    // The area filter owns the camera, and its results count as framed: lifting the filter must not
    // frame a result the user's own map move produced.
    if (areaActive) {
      framedBounds.current = pointsKey.current;
      return;
    }
    // The same result again — an SWR revalidation, say — is not a reason to move the camera.
    if (pointsKey.current === framedBounds.current) return;

    const timer = window.setTimeout(() => {
      const map = mapRef.current?.getMap();
      if (map == null) return;
      // Recorded only once the fit happens: StrictMode mounts this effect twice, and the second
      // run must schedule its own timer rather than skip it.
      framedBounds.current = pointsKey.current;
      framed.current = true;
      map.fitBounds(bounds, {
        duration: FRAME_DURATION_MS,
        maxZoom: FRAME_MAX_ZOOM,
        padding: EVENTS_PADDING,
      });
    }, FRAME_DELAY_MS);

    return () => clearTimeout(timer);
  }, [points, areaActive, resetToken]);

  const trackMove = (e: ViewStateChangeEvent, wholeWorld: boolean) => {
    const zoomed = e.viewState.zoom !== lastZoom.current;
    if (wholeWorld && isWholeWorld(e.target, lastZoom.current)) return;
    trackEvent('event_list_map', {
      action: wholeWorld
        ? 'zoom_out_full'
        : e.originalEvent?.type === 'click'
          ? 'zoom_button'
          : zoomed
            ? 'zoom'
            : 'pan',
    });
  };

  const onMoveEnd = (e: ViewStateChangeEvent) => {
    // Only user moves filter the list, not flyTo or the initial fit.
    const fromCluster = clusterZoom.current;
    clusterZoom.current = false;
    // Zoomed out all the way, the map shows the whole world: nothing to filter.
    const wholeWorld = isWholeWorld(e.target, e.viewState.zoom);
    if (e.originalEvent != null && !fromCluster) trackMove(e, wholeWorld);
    lastZoom.current = e.viewState.zoom;
    if (e.originalEvent == null && !fromCluster) return;
    const b = e.target.getBounds();
    if (b == null) return;
    if (wholeWorld) {
      onAreaChange(undefined);
      return;
    }
    onAreaChange(
      [
        round(clamp(b.getSouth(), 90)),
        round(clamp(b.getWest(), 180)),
        round(clamp(b.getNorth(), 90)),
        round(clamp(b.getEast(), 180)),
      ].join(','),
    );
  };

  /** A cluster is the bigger target, so prefer it when a dot sits under the same pointer. */
  const onMouseMove = (e: MapMouseEvent) => {
    const id =
      e.features?.find((f) => f.properties?.cluster_id != null)?.properties?.cluster_id ?? null;
    setHoveredCluster((prev) => (prev === id ? prev : id));
  };

  const onClick = (e: MapMouseEvent) => {
    const feature = e.features?.[0];
    if (feature == null || feature.geometry.type !== 'Point') return;
    const [lng, lat] = feature.geometry.coordinates;
    if (feature.properties?.cluster) {
      trackEvent('event_list_map', { action: 'cluster_click' });
      const source = mapRef.current?.getSource(SOURCE_ID) as GeoJSONSource | undefined;
      void source?.getClusterExpansionZoom(feature.properties.cluster_id).then((zoom) => {
        clusterZoom.current = true;
        mapRef.current?.easeTo({ center: [lng, lat], zoom });
      });
      return;
    }
    trackEvent('event_list_map', { action: 'point_click' });
    setPopup(points.find((p) => p.id === feature.properties?.id) ?? null);
  };

  return (
    <MapGL
      ref={mapRef}
      mapStyle={MAP_STYLE}
      initialViewState={{ bounds: WORLD_BOUNDS, fitBoundsOptions: { padding: WORLD_PADDING } }}
      style={{ width: '100%', height: MAP_HEIGHT }}
      transformRequest={transformRequest}
      onLoad={(e) => {
        e.target.setTransformConstrain(constrainToWorld(e.target));
        fitWorld(e.target);
      }}
      onResize={(e) => !areaActive && !framed.current && fitWorld(e.target)}
      renderWorldCopies={false}
      // On touch screens one finger scrolls the page, two fingers move the map.
      cooperativeGestures={isTouch}
      maxZoom={12}
      attributionControl={false}
      interactiveLayerIds={['clusters', 'points']}
      cursor={cursor}
      onMouseEnter={() => setCursor('pointer')}
      onMouseLeave={() => {
        setCursor('');
        setHoveredCluster(null);
      }}
      onMouseMove={onMouseMove}
      onClick={onClick}
      onMoveEnd={onMoveEnd}
    >
      <NavigationControl showCompass={false} />
      <Source
        id={SOURCE_ID}
        type="geojson"
        data={data}
        promoteId="id"
        cluster
        clusterRadius={45}
        clusterProperties={{
          live: ['+', ['case', ['==', ['get', 'status'], 'live'], 1, 0]],
          upcoming: ['+', ['case', ['==', ['get', 'status'], 'upcoming'], 1, 0]],
        }}
      >
        <Layer
          id="clusters"
          type="circle"
          filter={['has', 'point_count']}
          paint={{
            'circle-color': [
              'case',
              ['>', ['get', 'live'], 0],
              STATUS_COLORS.live,
              ['>', ['get', 'upcoming'], 0],
              STATUS_COLORS.upcoming,
              STATUS_COLORS.past,
            ],
            'circle-radius': ['step', ['get', 'point_count'], 14, 10, 17, 100, 20],
            'circle-stroke-color': [
              'case',
              ['==', ['get', 'cluster_id'], hoveredCluster ?? -1],
              HOVER_STROKE,
              '#fff',
            ],
            'circle-stroke-width': [
              'case',
              ['==', ['get', 'cluster_id'], hoveredCluster ?? -1],
              4,
              2,
            ],
          }}
        />
        <Layer
          id="cluster-count"
          type="symbol"
          filter={['has', 'point_count']}
          layout={{
            'text-field': ['get', 'point_count_abbreviated'],
            'text-font': ['Open Sans Semibold'],
            'text-size': 12,
            'text-allow-overlap': true,
          }}
          paint={{ 'text-color': '#fff' }}
        />
        <Layer
          id="points"
          type="circle"
          filter={['!', ['has', 'point_count']]}
          paint={{
            'circle-color': [
              'match',
              ['get', 'status'],
              'live',
              STATUS_COLORS.live,
              'upcoming',
              STATUS_COLORS.upcoming,
              STATUS_COLORS.past,
            ],
            'circle-radius': ['case', ['==', ['get', 'status'], 'live'], 7, 6],
            'circle-stroke-color': [
              'case',
              ['boolean', ['feature-state', 'hover'], false],
              HOVER_STROKE,
              '#fff',
            ],
            'circle-stroke-width': ['case', ['boolean', ['feature-state', 'hover'], false], 3, 1.5],
          }}
        />
      </Source>
      {popup && (
        <Popup
          longitude={popup.lng}
          latitude={popup.lat}
          offset={10}
          onClose={() => setPopup(null)}
          closeOnClick={false}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: 13 }}>
            <strong>{popup.name}</strong>
            <span>{dateRangeLabel(popup)}</span>
            <span style={{ color: STATUS_COLORS[statusOf(popup)] }}>
              {STATUS_LABELS[statusOf(popup)]}
            </span>
            <a
              href={`/player/${popup.slug || popup.id}/`}
              target="_blank"
              rel="noopener"
              onClick={() => trackEvent('event_list_map', { action: 'open_map' })}
            >
              Open map
            </a>
          </div>
        </Popup>
      )}
    </MapGL>
  );
};
